import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert } from '@arco-design/web-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './useAuth.jsx';
import { ADMIN_ROLE_SLUGS } from '../common/helpers/roleLabels';
import { apiClient } from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';
import { getWwwUrl } from '@/config/api';
import { GoogleOAuthProvider } from '@react-oauth/google';
import GoogleLoginButton from './GoogleLoginButton';
import LanguageSwitcher from '@/modules/common/components/LanguageSwitcher';
import { IconExport } from '@arco-design/web-react/icon';
import useRandomBackground from './useRandomBackground';
import './Auth.css';

const CorporateIcon = () => (
  <img src="/corporate-icon.png" alt="" width="22" height="22" style={{ objectFit: 'contain' }} />
);

const Login = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { loginWithGoogle, loading, error } = useAuth();
  const institutionalDomain = import.meta.env.VITE_INSTITUTIONAL_EMAIL_DOMAIN;
  const { url: bgImage, credit } = useRandomBackground();

  const { data: authConfig } = useQuery({
    queryKey: ['auth', 'config'],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.AUTH_CONFIG);
      return res.data;
    },
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  const showGoogle = authConfig?.oauth_provider === 'google' && authConfig?.google_client_id;

  const handleLoginSuccess = async (currentUser) => {
    const email = currentUser?.email?.toLowerCase() || '';
    const allowedDomain = (institutionalDomain || '').toLowerCase();
    const isUnieEmail = allowedDomain ? email.endsWith(`@${allowedDomain}`) : true;
    const activeRole = currentUser?.active_profile?.role?.name;
    const hasAdminRole = activeRole
      ? ADMIN_ROLE_SLUGS.includes(activeRole)
      : currentUser?.profiles?.some(profile => profile.is_active && ADMIN_ROLE_SLUGS.includes(profile.role?.name));

    if (hasAdminRole || isUnieEmail) {
      navigate('/dashboard');
      return;
    }

    try {
      const response = await apiClient.get(API_ENDPOINTS.ONBOARDING_STATUS);
      const submissionStatus = response?.data?.status;
      if (submissionStatus === 'draft' || submissionStatus === 'changes_requested') {
        navigate('/onboarding/wizard');
      } else {
        navigate('/onboarding/status');
      }
    } catch (err) {
      console.error('Error al cargar estado de onboarding:', err);
      navigate('/onboarding/wizard');
    }
  };

  const handleGoogleSuccess = async (credential) => {
    const result = await loginWithGoogle(credential);
    if (result.success) {
      await handleLoginSuccess(result.user);
    }
  };

  return (
    <div className="auth-container" style={{ backgroundImage: `url(${bgImage})` }}>
      {credit && (
        <span className="auth-photo-credit">
          Photo by{' '}
          <a href={`https://unsplash.com/photos/${credit.unsplash_photo_id}`} target="_blank" rel="noopener noreferrer">
            {credit.author}
          </a>
          {' / Unsplash'}
        </span>
      )}
      <div className="auth-card">
        <div className="auth-card-lang">
          <LanguageSwitcher />
        </div>
        <div className="auth-header">
          <h1>{t('login.title')}</h1>
          <p>{t('login.subtitle')}</p>
        </div>

        {error && (
          <Alert
            type="error"
            content={error}
            closable
            style={{ marginBottom: 20 }}
          />
        )}

        {showGoogle && (
          <GoogleOAuthProvider clientId={authConfig.google_client_id}>
            <GoogleLoginButton
              onSuccess={handleGoogleSuccess}
              onError={(msg) => console.error(msg)}
              disabled={loading}
            />
          </GoogleOAuthProvider>
        )}

        <Link to="/login/corporativo" className="corporate-login-button">
          <CorporateIcon />
          {t('login.corporateAccess')}
        </Link>

        <div className="auth-footer">
          <a href={getWwwUrl()} className="exit-button">
            <IconExport style={{ fontSize: 14 }} />
            {t('login.exit')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
